/**
 * tools 화면 라벨 — device-manager 현재화면 표시용(사람용 한글 라벨).
 * ============================================================
 * useHeartbeat 이 매 하트비트마다 현재 route → label 로 변환해 전송하고,
 * device-manager 가 device.current_screen_label 을 그대로 렌더한다.
 * (설계: 2026-06-18-device-manager / quest-tag 스팟 이름 보강)
 *
 * - 매핑된 route → 한글 라벨. quest-tag 는 선택된 스팟 이름까지 덧붙인다.
 * - 미매핑 route → 경로(pathname) 문자열 그대로(폴백 — 신규 화면 누락 시 식별 가능).
 */

// route(pathname) → 사람용 한글 라벨. tools/src/App.js 의 <Routes> 와 1:1.
// 명칭 출처: ServiceMenuPage 메뉴 title + 각 페이지 화면 제목(h1).
export const SCREEN_LABELS = Object.freeze({
  '/login': '로그인',
  '/store-select': '지점 선택',
  '/': '서비스 메뉴',
  '/all': '주렁은행',
  '/alimtalk': '알림톡',
  '/qrprint': 'QR 출력',
  '/qr-sticker': 'QR 스티커',
  '/qr-auto': 'QR스티커(자동)',
  '/events': '이벤트 관리',
  '/family': '가족 정보 변경',
  '/membership': '멤버십 가입',
  '/printer-manage': 'QR프린터관리',
  '/virtual-kiosk': '가상키오스크',
  '/coupon-redeem': '쿠폰 사용/취소',
  '/quest-tag': '퀘스트 미션',
  '/quest-status': '주키퍼 본부',
});

const QUEST_TAG_ROUTE = '/quest-tag';
const QUEST_SPOT_NAME_PREFIX = 'quest_spot_name:'; // 매장 네임스페이스. 'quest_spot:'(id) 와 분리.
const MAX_LABEL_LEN = 120; // device_heartbeats_latest.current_screen_label VARCHAR(120) 한계 보호.

// quest-tag 가 담당 스팟 이름을 영속하는 localStorage 키 — 매장(store_id) 단위.
// QuestTagPage(쓰기/삭제)와 buildScreenLabel(읽기)가 공유 → 키 단일 출처.
export function questSpotNameKey() {
  let storeId = 'unknown';
  try {
    storeId = localStorage.getItem('store_id') || 'unknown';
  } catch {
    /* localStorage 접근 불가 → unknown 네임스페이스 */
  }
  return `${QUEST_SPOT_NAME_PREFIX}${storeId}`;
}

// route 정규화 — 쿼리스트링/해시 제거 + 끝 슬래시 정리(루트 제외).
function normalizeRoute(route) {
  if (!route) return '/';
  let r = String(route).split('?')[0].split('#')[0];
  if (r.length > 1 && r.endsWith('/')) r = r.slice(0, -1);
  return r || '/';
}

/**
 * 현재 route → device-manager 표시용 라벨.
 * quest-tag 는 선택된 스팟 이름을 덧붙이고(미선택이면 '퀘스트 미션'),
 * 그 외 매핑 route 는 한글 라벨, 미매핑은 route 원문을 반환한다.
 *
 * @param {string} route - 현재 경로(pathname). 예: '/quest-tag'
 * @returns {string} 사람용 라벨(최대 120자).
 */
export function buildScreenLabel(route) {
  const r = normalizeRoute(route);
  let label;
  if (r === QUEST_TAG_ROUTE) {
    let name = '';
    try {
      name = localStorage.getItem(questSpotNameKey()) || '';
    } catch {
      /* swallow — 이름 없으면 기본 라벨 */
    }
    const base = SCREEN_LABELS[QUEST_TAG_ROUTE];
    label = name ? `${base} · ${name}` : base;
  } else {
    label = SCREEN_LABELS[r] || r;
  }
  return label.length > MAX_LABEL_LEN ? label.slice(0, MAX_LABEL_LEN) : label;
}
